/**
 * Boosts history page showing workspace boost usage and credit transactions.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Loader2,
  AlertCircle,
  ChevronRight,
  History,
  ChevronDown,
} from "lucide-react";
import Header from "./Header";
import BillingTabs from "./BillingTabs";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { useWorkspaceBoostHistory } from "../hooks/useAdminWorkspaces";
import { useWorkspaceCollaborators } from "../hooks/useWorkspaceMembers";
import { ledgerTypeKey, signedBoosts, BOOST_TRANSACTION_TYPES, STT_WORKFLOWS, txDescription, sttWorkflowLabel } from "../lib/boostLedger";
import { collaboratorName } from "../lib/collaborators";
import FilterSelect from "./FilterSelect";

const PAGE_LIMIT = 20;

type DatePreset = "today" | "yesterday" | "this_week" | "last_week" | "this_month" | "last_month" | "last_30" | "last_90" | "custom";

interface DateRange {
  start: string;
  end: string;
}

const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  today: "Today",
  yesterday: "Yesterday",
  this_week: "This Week",
  last_week: "Last Week",
  this_month: "This Month",
  last_month: "Last Month",
  last_30: "Last 30 Days",
  last_90: "Last 90 Days",
  custom: "Custom Range",
};

function toDateStr(d: Date): string {
  return d.toISOString().split("T")[0];
}

function getPresetRange(preset: DatePreset): DateRange {
  const now = new Date();
  const today = toDateStr(now);

  switch (preset) {
    case "today":
      return { start: today, end: today };
    case "yesterday": {
      const d = new Date(now);
      d.setDate(d.getDate() - 1);
      const yd = toDateStr(d);
      return { start: yd, end: yd };
    }
    case "this_week": {
      const d = new Date(now);
      d.setDate(d.getDate() - d.getDay());
      return { start: toDateStr(d), end: today };
    }
    case "last_week": {
      const d = new Date(now);
      d.setDate(d.getDate() - d.getDay() - 7);
      const start = toDateStr(d);
      d.setDate(d.getDate() + 6);
      return { start, end: toDateStr(d) };
    }
    case "this_month": {
      const d = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: toDateStr(d), end: today };
    }
    case "last_month": {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start: toDateStr(start), end: toDateStr(end) };
    }
    case "last_30": {
      const d = new Date(now);
      d.setDate(d.getDate() - 30);
      return { start: toDateStr(d), end: today };
    }
    case "last_90": {
      const d = new Date(now);
      d.setDate(d.getDate() - 90);
      return { start: toDateStr(d), end: today };
    }
    case "custom":
      return { start: "", end: "" };
  }
}

function formatDate(dateStr: string): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function BoostsHistory() {
  const { t } = useTranslation();
  const { activeWorkspace } = useWorkspace();
  const [preset, setPreset] = useState<DatePreset>("last_30");
  const [customRange, setCustomRange] = useState<DateRange>({ start: "", end: "" });
  const [type, setType] = useState("");
  const [userId, setUserId] = useState("");
  const [sttModel, setSttModel] = useState("");
  const [showPresets, setShowPresets] = useState(false);

  const range = preset === "custom" ? customRange : getPresetRange(preset);

  const { data: collaboratorsData } = useWorkspaceCollaborators();
  const members = collaboratorsData?.collaborators ?? [];

  // Any change to these restarts the list at offset 0 (they key the query).
  const {
    data,
    isLoading,
    error,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useWorkspaceBoostHistory(activeWorkspace?.id, {
    limit: PAGE_LIMIT,
    startDate: range.start || undefined,
    endDate: range.end || undefined,
    type: type || undefined,
    userId: userId || undefined,
    sttModel: sttModel || undefined,
  });

  const handlePresetChange = (p: DatePreset) => {
    setPreset(p);
    setShowPresets(false);
    if (p !== "custom") {
      setCustomRange({ start: "", end: "" });
    }
  };

  const handleCustomDateChange = (field: "start" | "end", value: string) => {
    setCustomRange((prev) => ({ ...prev, [field]: value }));
  };

  const transactions = data?.pages.flatMap((p) => p.transactions) ?? [];

  return (
    <div className="min-h-screen bg-muted">
      <Header />

      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-1">Billing</h1>
        {activeWorkspace && (
          <p className="text-sm text-muted-foreground mb-4">
            {activeWorkspace.name}
          </p>
        )}

        <BillingTabs />

        <div className="bg-card rounded-xl border">
          {/* Date range filter */}
          <div className="flex flex-wrap items-end gap-3 p-4 border-b border-border">
            <div className="relative">
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Period
              </label>
              <button
                onClick={() => setShowPresets(!showPresets)}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-border rounded-lg bg-background hover:bg-accent transition-colors min-w-[160px] justify-between"
              >
                {DATE_PRESET_LABELS[preset]}
                <ChevronDown className="h-3 w-3 text-muted-foreground" />
              </button>
              {showPresets && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowPresets(false)}
                  />
                  <div className="absolute top-full left-0 mt-1 z-20 bg-card border border-border rounded-lg shadow-lg py-1 min-w-[160px]">
                    {(Object.keys(DATE_PRESET_LABELS) as DatePreset[]).map((p) => (
                      <button
                        key={p}
                        onClick={() => handlePresetChange(p)}
                        className={`w-full text-left px-3 py-1.5 text-sm hover:bg-accent transition-colors ${
                          preset === p ? "font-medium text-foreground" : "text-muted-foreground"
                        }`}
                      >
                        {DATE_PRESET_LABELS[p]}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {preset === "custom" && (
              <>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    From
                  </label>
                  <input
                    type="date"
                    value={customRange.start}
                    onChange={(e) => handleCustomDateChange("start", e.target.value)}
                    className="px-3 py-1.5 text-sm border border-border rounded-lg bg-background"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    To
                  </label>
                  <input
                    type="date"
                    value={customRange.end}
                    onChange={(e) => handleCustomDateChange("end", e.target.value)}
                    className="px-3 py-1.5 text-sm border border-border rounded-lg bg-background"
                  />
                </div>
              </>
            )}

            <FilterSelect
              label={t("billing.ledger.filters.type")}
              value={type}
              onChange={setType}
              allLabel={t("billing.ledger.filters.allTypes")}
              options={BOOST_TRANSACTION_TYPES.map((v) => ({ value: v, label: t(`billing.ledger.types.${v}`) }))}
            />
            <FilterSelect
              label={t("billing.ledger.filters.member")}
              value={userId}
              onChange={setUserId}
              allLabel={t("billing.ledger.filters.allMembers")}
              options={members.map((c) => ({ value: c.user_id, label: collaboratorName(c) }))}
            />
            <FilterSelect
              label={t("billing.ledger.filters.workflow")}
              value={sttModel}
              onChange={setSttModel}
              allLabel={t("billing.ledger.filters.allWorkflows")}
              options={STT_WORKFLOWS.map((w) => ({ value: w.model, label: w.label }))}
            />
          </div>

          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">
                Loading boost history...
              </span>
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="flex items-center gap-3 p-4 m-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  Failed to load boost history
                </p>
                <p className="text-xs text-red-600">{error.message}</p>
              </div>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !error && transactions.length === 0 && (
            <div className="text-center py-12">
              <History className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                No boost activity found for this period
              </p>
            </div>
          )}

          {/* Transactions table */}
          {transactions.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="text-left px-4 py-2 font-medium text-muted-foreground">
                        Date
                      </th>
                      <th className="text-left px-4 py-2 font-medium text-muted-foreground">
                        Type
                      </th>
                      <th className="text-left px-4 py-2 font-medium text-muted-foreground">
                        User
                      </th>
                      <th className="text-left px-4 py-2 font-medium text-muted-foreground">
                        Feature
                      </th>
                      <th className="text-left px-4 py-2 font-medium text-muted-foreground">
                        {t("billing.ledger.filters.workflow")}
                      </th>
                      <th className="text-right px-4 py-2 font-medium text-muted-foreground">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx) => {
                      const isCredit = tx.amount > 0;
                      const labelKey = ledgerTypeKey(tx.type);
                      return (
                        <tr key={tx.id} className="border-t border-border">
                          <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                            {formatDate(tx.created_at)}
                          </td>
                          <td className="px-4 py-2">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                isCredit
                                  ? "bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300"
                                  : "bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300"
                              }`}
                            >
                              {labelKey ? t(labelKey) : isCredit ? "credit" : "usage"}
                            </span>
                          </td>
                          <td className="px-4 py-2 font-medium">
                            {tx.user_name}
                          </td>
                          <td className="px-4 py-2 text-muted-foreground">
                            {txDescription(tx)}
                          </td>
                          <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                            {sttWorkflowLabel(tx.stt_model)}
                          </td>
                          <td
                            className={`px-4 py-2 text-right font-medium ${
                              isCredit ? "text-green-600" : "text-orange-600"
                            }`}
                          >
                            {signedBoosts(tx.amount)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {hasNextPage && (
                <div className="py-4 flex justify-center border-t border-border">
                  <button
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    className="inline-flex items-center gap-1 px-4 py-2 text-sm text-foreground border border-border rounded-lg hover:bg-accent transition-colors disabled:opacity-50"
                  >
                    {isFetchingNextPage ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        Load more
                        <ChevronRight className="h-3 w-3" />
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
